import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import AdminSidebar from "../components/AdminSidebar";
import {
  Zap,
  Users,
  Briefcase,
  MessageSquare,
  BarChart3,
  Settings,
  Search,
  ChevronDown,
  Edit,
  Trash2,
  Shield,
  Crown,
  UserCheck,
  Building2,
  FileText,
  Video,
  RefreshCw,
  Download,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  LogOut,
  Home
} from "lucide-react";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [tiers, setTiers] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await fetch(`${API}/admin/stats`, { credentials: "include" });
      if (statsRes.ok) setStats(await statsRes.json());

      // Fetch users
      const usersRes = await fetch(`${API}/admin/users?limit=100`, { credentials: "include" });
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users);
      }

      // Fetch jobs
      const jobsRes = await fetch(`${API}/admin/jobs?limit=50`, { credentials: "include" });
      if (jobsRes.ok) {
        const data = await jobsRes.json();
        setJobs(data.jobs);
      }

      // Fetch logs
      const logsRes = await fetch(`${API}/admin/logs?limit=50`, { credentials: "include" });
      if (logsRes.ok) setLogs(await logsRes.json());

      // Fetch tiers
      const tiersRes = await fetch(`${API}/admin/subscription-tiers`, { credentials: "include" });
      if (tiersRes.ok) setTiers(await tiersRes.json());

    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const updateUserSubscription = async (userId, newTier) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/subscription`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ subscription_tier: newTier })
      });
      if (res.ok) {
        toast.success("Subscription updated!");
        fetchData();
        setShowUserModal(false);
      } else {
        toast.error("Failed to update subscription");
      }
    } catch (error) {
      toast.error("Failed to update subscription");
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        toast.success("Role updated!");
        fetchData();
        setShowUserModal(false);
      } else {
        toast.error("Failed to update role");
      }
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const updateJobStatus = async (jobId, newStatus) => {
    try {
      const res = await fetch(`${API}/admin/jobs/${jobId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success("Job status updated!");
        fetchData();
      } else {
        toast.error("Failed to update job status");
      }
    } catch (error) {
      toast.error("Failed to update job status");
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = !searchQuery || 
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = !roleFilter || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <AdminSidebar />
      
      <main className="lg:ml-64 min-h-screen px-4 sm:px-6 pb-6 pt-20 lg:pt-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
                ADMIN <span className="text-crimson">DASHBOARD</span>
              </h1>
              <p className="text-gray-400 text-sm sm:text-base">Platform overview and management</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button onClick={fetchData} variant="outline" className="border-[#333] text-white text-xs sm:text-sm">
                <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Refresh
              </Button>
              <div className="flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-obsidian-light border border-[#333]">
                <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500" />
                <span className="text-xs sm:text-sm text-gray-300 truncate max-w-[100px] sm:max-w-none">{user?.name}</span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 sm:gap-2 mb-6 sm:mb-8 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            {[
              { id: "overview", label: "Overview", icon: BarChart3 },
              { id: "users", label: "Users", icon: Users },
              { id: "jobs", label: "Jobs", icon: Briefcase },
              { id: "logs", label: "Activity Logs", icon: FileText },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}`}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "bg-crimson text-white"
                    : "bg-obsidian-light text-gray-400 hover:text-white"
                }`}
              >
                <tab.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Total Users" value={stats?.users?.total} />
                <StatCard icon={Building2} label="Recruiters" value={stats?.users?.recruiters} />
                <StatCard icon={UserCheck} label="Job Seekers" value={stats?.users?.jobseekers} />
                <StatCard icon={Shield} label="Admins" value={stats?.users?.admins} />
                <StatCard icon={Briefcase} label="Total Jobs" value={stats?.jobs?.total} />
                <StatCard icon={CheckCircle} label="Active Jobs" value={stats?.jobs?.active} />
                <StatCard icon={FileText} label="Applications" value={stats?.applications} />
                <StatCard icon={Video} label="Interviews" value={stats?.interviews} />
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link to="/admin/activity" className="bg-obsidian-light border border-[#222] p-4 hover:border-crimson/50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                      <Clock className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">User Activity</h4>
                      <p className="text-xs text-gray-500">Monitor real-time activity</p>
                    </div>
                  </div>
                </Link>
                <Link to="/admin/moderation" className="bg-obsidian-light border border-[#222] p-4 hover:border-crimson/50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-500/20 flex items-center justify-center group-hover:bg-yellow-500/30 transition-colors">
                      <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">Content Moderation</h4>
                      <p className="text-xs text-gray-500">Review reported content</p>
                    </div>
                  </div>
                </Link>
                <Link to="/admin/bulk" className="bg-obsidian-light border border-[#222] p-4 hover:border-crimson/50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                      <Users className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">Bulk Management</h4>
                      <p className="text-xs text-gray-500">Update multiple users</p>
                    </div>
                  </div>
                </Link>
                <Link to="/admin/export" className="bg-obsidian-light border border-[#222] p-4 hover:border-crimson/50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                      <Download className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">Data Export</h4>
                      <p className="text-xs text-gray-500">Download platform data</p>
                    </div>
                  </div>
                </Link>
              </div>

              {/* Subscription Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <h3 className="font-heading text-lg font-bold mb-4">Subscription Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Free Users</span>
                      <span className="text-2xl font-bold">{stats?.subscriptions?.free || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Paid Users</span>
                      <span className="text-2xl font-bold text-green-400">{stats?.subscriptions?.paid || 0}</span>
                    </div>
                    {(stats?.subscriptions?.ambassadors > 0) && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Ambassadors</span>
                        <span className="text-2xl font-bold text-emerald-400">{stats.subscriptions.ambassadors}</span>
                      </div>
                    )}
                    {(stats?.subscriptions?.discount > 0) && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Discount Users</span>
                        <span className="text-2xl font-bold text-amber-400">{stats.subscriptions.discount}</span>
                      </div>
                    )}
                    <div className="text-[10px] text-gray-600 uppercase tracking-wider">Excludes admins</div>
                    {/* Stacked bar */}
                    <div className="h-2 bg-obsidian rounded-full overflow-hidden flex">
                      {stats?.subscriptions?.paid > 0 && (
                        <div className="h-full bg-green-500" style={{ width: `${(stats.subscriptions.paid / (stats.users.total - (stats.users.admins || 0)) * 100) || 0}%` }} />
                      )}
                      {stats?.subscriptions?.ambassadors > 0 && (
                        <div className="h-full bg-emerald-500" style={{ width: `${(stats.subscriptions.ambassadors / (stats.users.total - (stats.users.admins || 0)) * 100) || 0}%` }} />
                      )}
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="bg-obsidian-light border border-[#222] p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <h3 className="font-heading text-lg font-bold mb-4">Recent Activity</h3>
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {logs.slice(0, 5).map((log, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 bg-crimson rounded-full" />
                        <span className="text-gray-400">{log.action}</span>
                        <span className="text-gray-500 text-xs ml-auto">
                          {new Date(log.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === "users" && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex gap-4 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-obsidian-light border-[#333] text-white"
                  />
                </div>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="px-4 py-2 bg-obsidian-light border border-[#333] text-white"
                >
                  <option value="">All Roles</option>
                  <option value="jobseeker">Job Seekers</option>
                  <option value="recruiter">Recruiters</option>
                  <option value="admin">Admins</option>
                </select>
              </div>

              {/* Users Table */}
              <div className="bg-obsidian-light border border-[#222] overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#333]">
                      <th className="text-left p-4 text-gray-400 font-medium">User</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Role</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Subscription</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Joined</th>
                      <th className="text-right p-4 text-gray-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.user_id} className="border-b border-[#222] hover:bg-white/5">
                        <td className="p-4">
                          <div>
                            <div className="font-medium text-white">{u.name}</div>
                            <div className="text-sm text-gray-500">{u.email}</div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-xs uppercase ${
                            u.role === "admin" ? "bg-yellow-500/20 text-yellow-400" :
                            u.role === "recruiter" ? "bg-blue-500/20 text-blue-400" :
                            "bg-green-500/20 text-green-400"
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-gray-300">{u.subscription_tier || "free"}</span>
                        </td>
                        <td className="p-4 text-gray-500 text-sm">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}
                        </td>
                        <td className="p-4 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#333] text-white"
                            onClick={() => {
                              setSelectedUser(u);
                              setShowUserModal(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Jobs Tab */}
          {activeTab === "jobs" && (
            <div className="space-y-4">
              <div className="bg-obsidian-light border border-[#222] overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#333]">
                      <th className="text-left p-4 text-gray-400 font-medium">Job</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Recruiter</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Status</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Created</th>
                      <th className="text-right p-4 text-gray-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <tr key={job.job_id} className="border-b border-[#222] hover:bg-white/5">
                        <td className="p-4">
                          <div>
                            <div className="font-medium text-white">{job.title}</div>
                            <div className="text-sm text-gray-500">{job.company} • {job.location}</div>
                          </div>
                        </td>
                        <td className="p-4 text-gray-400">
                          {job.recruiter?.name || "-"}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-xs uppercase ${
                            job.status === "active" ? "bg-green-500/20 text-green-400" :
                            job.status === "suspended" ? "bg-red-500/20 text-red-400" :
                            "bg-gray-500/20 text-gray-400"
                          }`}>
                            {job.status}
                          </span>
                        </td>
                        <td className="p-4 text-gray-500 text-sm">
                          {job.created_at ? new Date(job.created_at).toLocaleDateString() : "-"}
                        </td>
                        <td className="p-4 text-right space-x-2">
                          {job.status === "active" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-500/50 text-red-400"
                              onClick={() => updateJobStatus(job.job_id, "suspended")}
                            >
                              Suspend
                            </Button>
                          )}
                          {job.status === "suspended" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-green-500/50 text-green-400"
                              onClick={() => updateJobStatus(job.job_id, "active")}
                            >
                              Activate
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Logs Tab */}
          {activeTab === "logs" && (
            <div className="bg-obsidian-light border border-[#222] p-6">
              <h3 className="font-heading text-lg font-bold mb-4">Activity Logs</h3>
              <div className="space-y-3">
                {logs.map((log, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 bg-obsidian border border-[#333]">
                    <div className="w-10 h-10 bg-crimson/20 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-crimson" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{log.action}</span>
                        <span className="text-xs text-gray-500">by {log.admin_name}</span>
                      </div>
                      {log.target_user_id && (
                        <div className="text-sm text-gray-500">Target: {log.target_user_id}</div>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(log.timestamp).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* User Edit Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6 w-full max-w-md"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <h3 className="font-heading text-xl font-bold mb-4">Edit User</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Name</label>
                <div className="text-white">{selectedUser.name}</div>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Email</label>
                <div className="text-white">{selectedUser.email}</div>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Role</label>
                <select
                  value={selectedUser.role}
                  onChange={(e) => updateUserRole(selectedUser.user_id, e.target.value)}
                  className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white"
                >
                  <option value="jobseeker">Job Seeker</option>
                  <option value="recruiter">Recruiter</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Subscription Tier</label>
                <select
                  value={selectedUser.subscription_tier || ""}
                  onChange={(e) => updateUserSubscription(selectedUser.user_id, e.target.value)}
                  className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white"
                >
                  <optgroup label="Job Seeker Tiers">
                    {tiers?.jobseeker?.map(t => (
                      <option key={t.id} value={t.id}>{t.name} (${t.price}/mo)</option>
                    ))}
                  </optgroup>
                  <optgroup label="Recruiter Tiers">
                    {tiers?.recruiter?.map(t => (
                      <option key={t.id} value={t.id}>{t.name} (${t.price}/mo)</option>
                    ))}
                  </optgroup>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => setShowUserModal(false)}
                variant="outline"
                className="flex-1 border-[#333] text-white"
              >
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value }) => (
  <motion.div
    className="bg-obsidian-light border border-[#222] p-4"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
  >
    <div className="flex items-center gap-2 text-gray-400 mb-2">
      <Icon className="w-4 h-4" />
      <span className="text-xs">{label}</span>
    </div>
    <div className="text-2xl font-bold text-white">{value || 0}</div>
  </motion.div>
);

export default AdminDashboard;
